import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import compose from 'lodash/fp/compose';
import styled from 'styled-components';
import { useBlogCategories } from '../../../hooks/useBlog';
import { 
  createBlogCategory, 
  updateBlogCategory, 
  deleteBlogCategory, 
  getBlogCategories 
} from '../../../actions';
import { 
  Form, 
  Input, 
  TextArea, 
  ButtonLink,
} from '../../common/FormItems';
import { withTranslation, withUserPreferences } from '../../hoc';
import { Pencil, Trash, Plus, CheckCircle, XCircle } from 'react-bootstrap-icons';
import Swal from 'sweetalert2';

const Wrapper = styled.div`
  width: 95%;
  margin: 1.5% auto;
  padding: 0 80px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--admincat-color-grey-3);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  color: var(--admincat-color-grey-2);
  font-weight: 600;
  margin: 0;
`;

const CreateButton = styled.button`
  background: linear-gradient(135deg, #4C81BE 0%, #3F729B 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(76, 129, 190, 0.3);
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(76, 129, 190, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const CategoryCard = styled.div`
  background: var(--admincat-color-grey-1);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const CategoryName = styled.h3`
  font-size: 1.3rem;
  color: var(--admincat-color-grey-2);
  margin: 0;
  font-weight: 600;
  flex: 1;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.4rem 0.9rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => props.active 
    ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' 
    : 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)'};
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  svg {
    width: 12px;
    height: 12px;
  }
`;

const CategoryDescription = styled.p`
  color: var(--admincat-color-grey-3);
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0 0 1rem 0;
`;

const CategoryFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid var(--admincat-color-grey-3);
`;

const PostsCount = styled.div`
  font-size: 0.9rem;
  color: var(--admincat-color-grey-3);
  
  strong {
    color: var(--admincat-color-grey-2);
    font-size: 1.1rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  background: ${props => props.danger 
    ? 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)' 
    : 'linear-gradient(135deg, #4C81BE 0%, #3F729B 100%)'};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const FormSection = styled.div`
  position: sticky;
  top: 2rem;
  height: fit-content;
`;

const FormWrapper = styled.div`
  background: var(--admincat-color-grey-1);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const FormTitle = styled.h2`
  font-size: 1.5rem;
  color: var(--admincat-color-grey-2);
  font-weight: 600;
  margin: 0 0 1.5rem 0;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--admincat-color-grey-3);
`;

const SwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const SwitchLabel = styled.label`
  font-size: 1rem;
  font-weight: 600;
  color: var(--admincat-color-grey-2);
  margin: 0;
`;

const SwitchToggle = styled.div`
  position: relative;
  width: 50px;
  height: 26px;
  background: ${props => props.checked ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' : '#ccc'};
  border-radius: 34px;
  cursor: pointer;
  transition: all 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: white;
    top: 3px;
    left: ${props => props.checked ? '27px' : '3px'};
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const HelpText = styled.p`
  font-size: 0.85rem;
  color: var(--admincat-color-grey-3);
  margin: 0.5rem 0 0 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: var(--admincat-color-grey-1);
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

  h3 {
    font-size: 1.5rem;
    color: var(--admincat-color-grey-3);
    margin-bottom: 1rem;
  }

  p {
    color: var(--admincat-color-grey-3);
    margin-bottom: 0;
  }
`;

const LoadingState = styled(EmptyState)`
  .spinner {
    width: 50px;
    height: 50px;
    border: 4px solid var(--admincat-color-grey-3);
    border-top-color: #4C81BE;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 1rem;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const FormButtonsWrapper = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const CancelButton = styled.button`
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.65rem 1.5rem;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);

  &:hover {
    background: linear-gradient(135deg, #c82333 0%, #bd2130 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(220, 53, 69, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const BlogCategoryManage = (props) => {
  const { tr } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { categories, isLoading, refetch } = useBlogCategories();
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Force refresh on component mount
  useEffect(() => {
    refetch();
  }, []);

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      is_active: category.is_active !== undefined ? category.is_active : true,
    });
    // Scroll to form on mobile
    if (window.innerWidth < 1024) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCancelEdit = () => {
    setEditingCategory(null);
    setFormData({ name: '', description: '', is_active: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (editingCategory) {
        await dispatch(updateBlogCategory(editingCategory.slug, formData));
        Swal.fire(tr('Success'), tr('Category updated successfully'), 'success');
      } else {
        await dispatch(createBlogCategory(formData));
        Swal.fire(tr('Success'), tr('Category created successfully'), 'success');
      }
      handleCancelEdit();
      // Force refresh after successful submit
      await refetch();
    } catch (error) {
      Swal.fire(tr('Error'), tr('Failed to save category'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (category, e) => {
    e.stopPropagation();
    const result = await Swal.fire({
      title: tr('Delete Category?'),
      text: category.posts_count > 0 
        ? tr(`This category has ${category.posts_count} posts. Are you sure?`)
        : tr('Are you sure you want to delete this category?'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deleteBlogCategory(category.slug));
        if (editingCategory?.id === category.id) {
          handleCancelEdit();
        }
        // Force refresh after successful delete
        await refetch();
        Swal.fire(tr('Success'), tr('Category deleted successfully'), 'success');
      } catch (error) {
        Swal.fire(tr('Error'), tr('Failed to delete category'), 'error');
      }
    }
  };

  const categoriesData = categories?.results || categories || [];

  if (isLoading) {
    return (
      <Wrapper>
        <LoadingState>
          <div className="spinner" />
          <h3>{tr('Loading categories...')}</h3>
        </LoadingState>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Header>
        <Title>{tr('Blog Categories')}</Title>
      </Header>

      <ContentGrid>
        <div>
          {categoriesData.length === 0 ? (
            <EmptyState>
              <h3>{tr('No categories found')}</h3>
              <p>{tr('Create your first category using the form on the right')}</p>
            </EmptyState>
          ) : (
            <CategoryGrid>
              {categoriesData.map((category) => (
                <CategoryCard key={category.id}>
                  <CategoryHeader>
                    <CategoryName>{category.name}</CategoryName>
                    <StatusBadge active={category.is_active}>
                      {category.is_active ? <CheckCircle /> : <XCircle />}
                      {tr(category.is_active ? 'Active' : 'Inactive')}
                    </StatusBadge>
                  </CategoryHeader>
                  <CategoryDescription>
                    {category.description || tr('No description provided')}
                  </CategoryDescription>
                  <CategoryFooter>
                    <PostsCount>
                      <strong>{category.posts_count || 0}</strong> {tr('posts')}
                    </PostsCount>
                    <ActionButtons>
                      <ActionButton onClick={() => handleEdit(category)}>
                        <Pencil /> {tr('Edit')}
                      </ActionButton>
                      <ActionButton danger onClick={(e) => handleDelete(category, e)}>
                        <Trash /> {tr('Delete')}
                      </ActionButton>
                    </ActionButtons>
                  </CategoryFooter>
                </CategoryCard>
              ))}
            </CategoryGrid>
          )}
        </div>

        <FormSection>
          <FormWrapper>
            <FormTitle>
              {editingCategory ? tr('Edit Category') : tr('Add New Category')}
            </FormTitle>
            <Form
              onSubmit={handleSubmit}
              formData={formData}
              isLoading={isSubmitting}
              title=""
            >
              <Input
                name="name"
                label={tr('Category Name')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                showRequiredAsterisk
                placeholder={tr('Enter category name')}
              />

              <TextArea
                name="description"
                label={tr('Description')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                placeholder={tr('Enter category description (optional)')}
              />

              <div style={{ marginTop: '1.5rem' }}>
                <SwitchWrapper>
                  <SwitchToggle 
                    checked={formData.is_active}
                    onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                  />
                  <SwitchLabel>{tr('Active Status')}</SwitchLabel>
                </SwitchWrapper>
                <HelpText>
                  {tr('Only active categories will be visible when creating posts')}
                </HelpText>
              </div>

              {/* {editingCategory && (
                <FormButtonsWrapper>
                  <CancelButton type="button" onClick={handleCancelEdit}>
                    {tr('Cancel Edit')}
                  </CancelButton>
                </FormButtonsWrapper>
              )} */}
            </Form>
          </FormWrapper>
        </FormSection>
      </ContentGrid>
    </Wrapper>
  );
};

export default compose(
  withTranslation,
  withUserPreferences,
)(BlogCategoryManage);